/**
 * LaunchSetupPanel — assign workload templates, target windows, and owning org
 * to priorities. This is the input surface that makes placements real instead
 * of "assumed demand".
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Download, FileDown, Loader2, Search, Upload, Wand2 } from 'lucide-react';
import type { Priority } from '../../../lib/planningEngineModule.types';
import type { LaunchProfile } from '../../../lib/timePhasedPlanning.types';
import { getScopedPriorities } from '../../../lib/planningEngineService';
import {
  updatePriorityLaunchSetup,
  exportLaunchSetupCsv,
  importLaunchSetupCsv,
  launchSetupCsvTemplate,
} from '../../../lib/timePhasedPlanningService';
import { getItemCountState } from '../../collection/types';
import { downloadTextFile } from './timePhasedUiShared';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { formatOrgUnitPathLabel } from '../../../lib/orgUnitTree';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { usePlanningEngine } from '../../../pages/planning/engine/PlanningEngineContext';
import { assumptionsFor, loadPatternContext, type PatternContext } from '../../../lib/typePatternsService';
import { ensurePatternWorkloadTemplate } from '../../../lib/timePhasedPlanningService';
import type { SourcedWorkloadLine } from '../../../lib/typePatterns';

const CARD =
  'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] ';

export function LaunchSetupPanel({
  projectId,
  scenarioId,
  profiles,
  onChanged,
}: {
  projectId: string;
  scenarioId: string;
  profiles: LaunchProfile[];
  onChanged?: () => void;
}) {
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const orgUnitsMap = useEnterpriseIdentityStore((s) => s.orgUnits);
  const fetchOrgUnits = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const { scopedItemIds } = usePlanningEngine();

  useEffect(() => {
    void fetchOrgUnits();
  }, [fetchOrgUnits]);

  const orgUnitOptions = useMemo(() => {
    return [...orgUnitsMap.values()]
      .filter((ou) => ou.active !== false)
      .filter((ou) => !workspaceId || !ou.workspaceId || ou.workspaceId === workspaceId)
      .map((ou) => ({
        id: ou.id,
        label: formatOrgUnitPathLabel(ou, orgUnitsMap),
        sortKey: formatOrgUnitPathLabel(ou, orgUnitsMap),
      }))
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [orgUnitsMap, workspaceId]);

  const loadPriorities = useCallback(async () => {
    await fetchItems();
    const liveItems = useItemsStore.getState().items;
    const list = await getScopedPriorities(projectId, liveItems, scenarioId, scopedItemIds);
    list.sort((a, b) => (a.forceRank ?? 9999) - (b.forceRank ?? 9999));
    return list;
  }, [projectId, scenarioId, scopedItemIds, fetchItems]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadPriorities()
      .then((list) => {
        if (!cancelled) setPriorities(list);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [loadPriorities]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return priorities;
    return priorities.filter((p) => {
      if (p.name.toLowerCase().includes(q)) return true;
      if (p.owningOrg?.toLowerCase().includes(q)) return true;
      if (p.owningOrgUnitId) {
        const ou = orgUnitsMap.get(p.owningOrgUnitId);
        if (ou && formatOrgUnitPathLabel(ou, orgUnitsMap).toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [priorities, search, orgUnitsMap]);

  // Type patterns: the date-anchored workload an item's types say it usually takes.
  const [patternCtx, setPatternCtx] = useState<PatternContext | null>(null);
  const [extraProfiles, setExtraProfiles] = useState<LaunchProfile[]>([]);
  const priorityIdKey = priorities.map((p) => p.id).join(',');
  useEffect(() => {
    if (!priorityIdKey) return;
    let live = true;
    loadPatternContext(projectId, priorityIdKey.split(','))
      .then((ctx) => live && setPatternCtx(ctx))
      .catch(() => live && setPatternCtx(null));
    return () => {
      live = false;
    };
  }, [projectId, priorityIdKey]);
  const patternLinesFor = useCallback(
    (id: string): SourcedWorkloadLine[] => {
      if (!patternCtx) return [];
      const item = useItemsStore.getState().items.find((i) => i.id === id) as
        | { id: string; work_type?: string | null; launch_type?: string | null; investment_type?: string | null }
        | undefined;
      if (!item) return [];
      return assumptionsFor(patternCtx, item).workload.filter((l) => l.anchor === 'date');
    },
    [patternCtx],
  );
  const allProfiles = useMemo(
    () => [...profiles, ...extraProfiles.filter((e) => !profiles.some((p) => p.id === e.id))],
    [profiles, extraProfiles],
  );
  const applyPatternWorkload = async (id: string) => {
    const t = await ensurePatternWorkloadTemplate(projectId, patternLinesFor(id));
    if (!t) return;
    setExtraProfiles((prev) => (prev.some((p) => p.id === t.id) ? prev : [...prev, t]));
    await save(id, { launchProfileId: t.id });
  };

  const countState = getItemCountState(priorities.length);
  const unassigned = priorities.filter((p) => !p.launchProfileId);
  const defaultProfileId = profiles[0]?.id;

  const save = async (
    id: string,
    fields: Parameters<typeof updatePriorityLaunchSetup>[1],
  ) => {
    setSavingId(id);
    setSavedId(null);
    try {
      await updatePriorityLaunchSetup(id, fields);
      setPriorities((prev) =>
        prev.map((p) =>
          p.id === id
            ? {
                ...p,
                launchProfileId:
                  'launchProfileId' in fields ? fields.launchProfileId ?? null : p.launchProfileId,
                targetLaunchStart:
                  'targetLaunchStart' in fields
                    ? fields.targetLaunchStart ?? null
                    : p.targetLaunchStart,
                targetLaunchEnd:
                  'targetLaunchEnd' in fields ? fields.targetLaunchEnd ?? null : p.targetLaunchEnd,
                owningOrg: 'owningOrg' in fields ? fields.owningOrg ?? null : p.owningOrg,
                owningOrgUnitId:
                  'owningOrgUnitId' in fields
                    ? fields.owningOrgUnitId ?? null
                    : p.owningOrgUnitId,
              }
            : p,
        ),
      );
      setSavedId(id);
      onChanged?.();
      setTimeout(() => setSavedId((cur) => (cur === id ? null : cur)), 2000);
    } finally {
      setSavingId(null);
    }
  };

  const handleAssignAllDefault = async () => {
    if (!defaultProfileId || unassigned.length === 0) return;
    setBulkSaving(true);
    try {
      await Promise.all(
        unassigned.map((p) => updatePriorityLaunchSetup(p.id, { launchProfileId: defaultProfileId })),
      );
      setPriorities((prev) =>
        prev.map((p) => (p.launchProfileId ? p : { ...p, launchProfileId: defaultProfileId })),
      );
      onChanged?.();
    } finally {
      setBulkSaving(false);
    }
  };

  const profileNameById = useMemo(
    () => new Map(profiles.map((p) => [p.id, p.name])),
    [profiles],
  );

  const handleExport = () => {
    downloadTextFile(
      'launch-setup.csv',
      exportLaunchSetupCsv(
        priorities.map((p) => ({
          name: p.name,
          forceRank: p.forceRank,
          launchProfileName: p.launchProfileId
            ? profileNameById.get(p.launchProfileId)
            : undefined,
          targetLaunchStart: p.targetLaunchStart,
          targetLaunchEnd: p.targetLaunchEnd,
          owningOrg: p.owningOrg,
        })),
      ),
    );
  };

  const handleImportFile = async (file: File) => {
    setImportMessage(null);
    const text = await file.text();
    const result = await importLaunchSetupCsv(projectId, scenarioId, text);
    setPriorities(await loadPriorities());
    onChanged?.();
    setImportMessage(
      result.unmatched.length
        ? `Updated ${result.updated}. Unmatched: ${result.unmatched.slice(0, 5).join(', ')}${
            result.unmatched.length > 5 ? '…' : ''
          }`
        : `Updated ${result.updated} priorit${result.updated === 1 ? 'y' : 'ies'} from CSV.`,
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
      </div>
    );
  }

  if (countState === 'zero') {
    return (
      <div className={`${CARD} p-6 text-center text-sm text-[var(--text-ter)]`}>
        No priorities in this scenario yet. Add priorities in the Planning Engine first.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] space-y-1">
          {unassigned.length === 0 ? (
            <span className="inline-flex items-center gap-1 text-[var(--green)] dark:text-[var(--text-ter)]">
              <Check className="h-4 w-4" /> Every priority has a workload template.
            </span>
          ) : (
            <p>
              <strong>{unassigned.length}</strong> of {priorities.length} priorities have no launch
              profile — they get assumed demand until assigned.
            </p>
          )}
          <p className="text-xs text-[var(--text-ter)]">
            Manage the profile library in{' '}
            <Link
              to="/prioritization/settings/workload-templates"
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              Settings → Workload Templates
            </Link>
            .
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {unassigned.length > 0 && defaultProfileId && (
            <button
              type="button"
              disabled={bulkSaving}
              onClick={() => void handleAssignAllDefault()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-primary-300 text-primary-700 dark:text-primary-300 dark:border-primary-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-50"
            >
              {bulkSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
              Assign &ldquo;{profiles[0].name}&rdquo; to all unassigned
            </button>
          )}
          <button
            type="button"
            onClick={() => downloadTextFile('launch-setup-template.csv', launchSetupCsvTemplate())}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--rule)] dark:border-[var(--rule)]"
          >
            <FileDown className="h-3.5 w-3.5" />
            Template
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={priorities.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--rule)] dark:border-[var(--rule)] disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            Export
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[var(--rule)] dark:border-[var(--rule)]"
          >
            <Upload className="h-3.5 w-3.5" />
            Import CSV
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleImportFile(f);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {importMessage && (
        <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">{importMessage}</p>
      )}

      {countState === 'many' && (
        <div className="relative max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search priorities…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]"
          />
        </div>
      )}

      <div className={CARD}>
        {/* Header row — hidden on small screens where rows stack */}
        <div className="hidden md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_repeat(2,130px)_minmax(0,1fr)_28px] gap-3 px-4 py-2 border-b border-[var(--rule)] dark:border-[var(--rule)] text-[11px] font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <div>Priority</div>
          <div>Workload template</div>
          <div>Target start</div>
          <div>Target launch</div>
          <div>Owning org</div>
          <div />
        </div>
        <div className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_repeat(2,130px)_minmax(0,1fr)_28px] gap-2 md:gap-3 px-4 py-3 items-center"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                  {p.forceRank != null && (
                    <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)] mr-1.5">
                      #{p.forceRank}
                    </span>
                  )}
                  {p.name}
                </div>
                {!p.launchProfileId && (
                  <div className="text-[11px] text-[var(--mustard)] dark:text-[var(--mustard)]">
                    Assumed demand — assign a template
                  </div>
                )}
                {(() => {
                  const lines = patternLinesFor(p.id);
                  if (lines.length === 0) return null;
                  const from = [...new Set(lines.map((l) => l.source.typeLabel))].join(' + ');
                  return (
                    <button
                      type="button"
                      onClick={() => void applyPatternWorkload(p.id)}
                      className="mt-0.5 text-[11px] text-[var(--text-sec)] underline hover:text-[var(--text-pri)]"
                      title={lines.map((l) => `${l.resourceType}: ${l.capacityUnits} CU, weeks ${l.fromWeek} to ${l.toWeek} (${l.source.typeLabel})`).join('\n')}
                    >
                      Use the {from} pattern's workload
                    </button>
                  );
                })()}
              </div>
              <select
                value={p.launchProfileId ?? ''}
                onChange={(e) =>
                  void save(p.id, { launchProfileId: e.target.value || null })
                }
                className="text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-2 py-1.5 w-full"
                aria-label={`Workload template for ${p.name}`}
              >
                <option value="">No profile (assumed)</option>
                {allProfiles.map((prof) => (
                  <option key={prof.id} value={prof.id}>
                    {prof.name}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={p.targetLaunchStart ?? ''}
                onChange={(e) =>
                  void save(p.id, { targetLaunchStart: e.target.value || null })
                }
                className="text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-2 py-1.5 w-full"
                aria-label={`Target window start for ${p.name}`}
              />
              <input
                type="date"
                value={p.targetLaunchEnd ?? ''}
                onChange={(e) =>
                  void save(p.id, { targetLaunchEnd: e.target.value || null })
                }
                className="text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-2 py-1.5 w-full"
                aria-label={`Target launch date for ${p.name}`}
              />
              <select
                value={p.owningOrgUnitId ?? ''}
                onChange={(e) => {
                  const id = e.target.value || null;
                  const ou = id ? orgUnitsMap.get(id) : undefined;
                  void save(p.id, {
                    owningOrgUnitId: id,
                    owningOrg: ou
                      ? formatOrgUnitPathLabel(ou, orgUnitsMap)
                      : null,
                  });
                }}
                className="text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-2 py-1.5 w-full"
                aria-label={`Owning org for ${p.name}`}
              >
                <option value="">No org unit</option>
                {orgUnitOptions.map((ou) => (
                  <option key={ou.id} value={ou.id}>
                    {ou.label}
                  </option>
                ))}
              </select>
              <div className="h-5 w-5 justify-self-end md:justify-self-auto">
                {savingId === p.id && <Loader2 className="h-4 w-4 animate-spin text-[var(--text-ter)]" />}
                {savedId === p.id && <Check className="h-4 w-4 text-[var(--green)]" />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
